"use client";

import { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CheckIcon, CircleAlertIcon, CopyXIcon, LoaderCircleIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useGridFields, resolveGridData, type GridColumn, type GridRow } from "@/components/admin/grid-fields-editor";
import { SettingsConfigKvBlock } from "@/components/admin/settings-field";
import { AdminDialog } from "@/components/admin/admin-dialog-layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { InputGroup, InputGroupInput, InputGroupAddon } from "@/components/ui/input-group";
import { Progress } from "@/components/ui/progress";
import { HoverTip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { createRemoteAttachTask, type RemoteAttachTask, type RemotePasteResult } from "@/lib/upload/remote-attach-client";

function RemoteUrlCell({ task, value, busy, disabled, label, placeholder, onChange }: {
  task?: RemoteAttachTask; value: string; busy: boolean; disabled: boolean;
  label: string; placeholder: string; onChange: (value: string) => void;
}) {
  const t = useTranslations("site.attach");
  const status = task?.status ?? "idle";
  const active = status === "uploading";
  const percent = status === "success" ? 100 : task?.progress?.percent ?? null;
  const error = status === "error";
  const duplicate = status === "duplicate";
  const description = error ? task?.error : duplicate ? t("remoteDuplicate") : status === "success" ? t("uploadSuccess") : undefined;

  return (
    <InputGroup data-remote-row="" data-status={status} className={cn("h-7 overflow-hidden", error && "border-destructive/50")}>
      {active || status === "success" || error ? (
        <Progress
          value={error ? 100 : percent}
          aria-label={label}
          className="pointer-events-none absolute inset-0"
          trackClassName="h-full rounded-none bg-transparent"
          indicatorClassName={cn("rounded-none bg-primary/12", active && percent === null && "w-2/5 animate-pulse", status === "success" && "bg-emerald-500/15", error && "bg-destructive/12")}
        />
      ) : null}
      <InputGroupInput
        value={value}
        aria-label={label}
        aria-invalid={error || undefined}
        placeholder={placeholder}
        readOnly={busy || status === "success"}
        disabled={disabled}
        onChange={event => onChange(event.target.value)}
        className="relative h-7 bg-transparent px-2 text-xs dark:bg-transparent"
      />
      {active || status === "success" || error || duplicate ? (
        <InputGroupAddon align="inline-end" className="relative shrink-0 cursor-default gap-1.5 px-2 py-0 text-xs tabular-nums">
          {active ? <LoaderCircleIcon className="size-3 animate-spin" aria-hidden /> : null}
          {status === "success" || error || duplicate ? (
            <HoverTip content={description}>
              <span tabIndex={0} role="status" aria-label={description} className={cn("inline-flex outline-none", status === "success" && "text-emerald-600 dark:text-emerald-400", error && "text-destructive")}>
                {status === "success" ? <CheckIcon className="size-3.5" /> : error ? <CircleAlertIcon className="size-3.5" /> : <CopyXIcon className="size-3.5" />}
              </span>
            </HoverTip>
          ) : null}
          {(active || status === "success") && percent !== null ? <span>{percent}%</span> : null}
        </InputGroupAddon>
      ) : null}
    </InputGroup>
  );
}

export function AttachRemoteQueue({ tasks, busy, disabled, canUpload, progress, placeholder, hint, onChange, onPaste, onUpload, onCancel }: {
  tasks: RemoteAttachTask[]; busy: boolean; disabled: boolean; canUpload: boolean;
  progress: { completed: number; total: number }; placeholder: string; hint?: string;
  onChange: (rows: Array<{ id: string; url: string }>) => void;
  onPaste: (text: string) => RemotePasteResult;
  onUpload: () => void; onCancel: () => void;
}) {
  const t = useTranslations("site.attach");
  const containerRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLParagraphElement>(null);
  const rows = useMemo(() => tasks.map(task => ({ id: task.id, cells: { url: task.url } })), [tasks]);
  const lookup = useMemo(() => new Map(tasks.map((task, index) => [task.id, { task, index }])), [tasks]);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteError, setPasteError] = useState<Extract<RemotePasteResult, { ok: false }> | null>(null);
  const pasteConfirmed = useRef(false);
  const errorId = useId();

  useLayoutEffect(() => {
    const container = containerRef.current;
    const actions = actionsRef.current;
    if (!container || !actions) return;
    const measure = () => {
      const body = container.querySelector<HTMLElement>("[data-grid-scroll]");
      if (!body || container.getBoundingClientRect().height === 0) return;
      const bottom = hintRef.current ? hintRef.current.getBoundingClientRect().top - 8 : container.getBoundingClientRect().bottom;
      const height = Math.max(0, Math.floor(bottom - body.getBoundingClientRect().top - 11));
      container.style.setProperty("--attach-remote-body-height", `${height}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(actions);
    if (hintRef.current) observer.observe(hintRef.current);
    return () => observer.disconnect();
  }, []);

  const emitRows = (next: GridRow[]) => onChange(next.map(row => ({ id: row.id, url: row.cells.url ?? "" })));
  const columns: GridColumn[] = [{
    key: "url", label: t("remoteAddresses"), locked: true,
    renderCell: ({ row, value, onChange }) => <RemoteUrlCell
      task={lookup.get(row.id)?.task} value={value} busy={busy} disabled={disabled}
      label={t("remoteUrlRow", { index: (lookup.get(row.id)?.index ?? 0) + 1 })}
      placeholder={placeholder} onChange={onChange}
    />,
  }];
  const { toolbar, table } = useGridFields({
    columns, rows, canAddColumn: false, canAddRow: !busy, disabled,
    bare: true, hideHeader: true, sortableRows: !busy,
    maxBodyHeight: "var(--attach-remote-body-height, 25.625rem)",
    onAddRow: () => {
      const task = createRemoteAttachTask();
      emitRows([...rows, { id: task.id, cells: { url: "" } }]);
    },
    onChange: next => {
      const result = resolveGridData(next, { columns, rows });
      emitRows(result.rows);
    },
  });

  function confirmPaste() {
    if (pasteConfirmed.current) return;
    pasteConfirmed.current = true;
    const result = onPaste(pasteText);
    if (!result.ok) { pasteConfirmed.current = false; setPasteError(result); return; }
    setPasteOpen(false);
  }
  const errorText = !pasteError ? "" : pasteError.reason === "invalid" || pasteError.reason === "ambiguous"
    ? t(pasteError.reason === "invalid" ? "remotePasteInvalid" : "remotePasteAmbiguous", pasteError.issue)
    : pasteError.reason === "empty" ? t("urlPlaceholder", { kind: "file" }) : t("remotePasteUnavailable");

  return (
    <>
      <div ref={containerRef} className="flex min-h-0 flex-1 flex-col gap-2">
        <div className="min-h-0 flex-1">
          <SettingsConfigKvBlock label={t("remoteAddresses")} variant="panel" action={
            <div ref={actionsRef} className="flex max-w-full flex-wrap items-center justify-end gap-3">
              {toolbar}
              <Button type="button" variant="link" className="h-5 rounded-none p-0 text-sm font-normal" disabled={disabled || busy} onClick={() => {
                pasteConfirmed.current = false; setPasteError(null); setPasteText(""); setPasteOpen(true);
              }}>{t("remotePaste")}</Button>
              {busy ? <span role="status" className="text-xs tabular-nums text-muted-foreground">{progress.completed}/{progress.total}</span> : null}
              <Button type="button" variant="link" className="h-5 rounded-none p-0 text-sm font-normal" disabled={disabled || !busy && !canUpload} onClick={busy ? onCancel : onUpload}>
                {busy ? t("remoteStop") : t("remoteStart")}
              </Button>
            </div>
          }>{table}</SettingsConfigKvBlock>
        </div>
        {hint ? <HoverTip content={hint}><p ref={hintRef} className="shrink-0 truncate text-xs text-muted-foreground">{hint}</p></HoverTip> : null}
      </div>
      <AdminDialog open={pasteOpen} onOpenChange={setPasteOpen} title={t("remotePasteTitle")} size="md"
        confirm={{ dirty: Boolean(pasteText.trim()) && !disabled && !busy, onClick: confirmPaste }}>
        <Textarea autoFocus value={pasteText} aria-label={t("remoteAddresses")} placeholder={t("remotePastePlaceholder")}
          aria-invalid={Boolean(pasteError) || undefined} aria-describedby={pasteError ? errorId : undefined}
          className="h-64 max-h-[45dvh] resize-none" onChange={event => { setPasteText(event.target.value); setPasteError(null); }} />
        {pasteError ? <p id={errorId} role="alert" className="pt-2 text-sm text-destructive">{errorText}</p> : null}
      </AdminDialog>
    </>
  );
}
